import React from "react";
import {
  formatInvoiceDateShort,
  formatStatusMonthDay,
  getInvoiceDisplayStatus,
  invoiceDaysLate,
} from "../../utils/invoiceMonthDisplay";

const pill = (className, children) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-medium ${className}`}>
    {children}
  </span>
);

const mutedSub = (text) => (
  <span className="text-[11px] text-[var(--muted,#6b6a66)] leading-snug">{text}</span>
);

const InvoiceStatusCell = ({ inv, noteSettings }) => {
  const invoiceDate = formatInvoiceDateShort(inv?.invoice_date);
  const lastInvoiceDate = formatInvoiceDateShort(inv?.prior_invoice_date);
  const status = getInvoiceDisplayStatus(inv, noteSettings);
  const expectedLabel = formatStatusMonthDay(inv?.expected_deadline || inv?.expected_invoice_date);
  const daysLate = invoiceDaysLate(inv);

  if (status === "expected") {
    return (
      <div className="flex flex-col items-start gap-0.5 min-w-0">
        {mutedSub(expectedLabel ? `Expected by ${expectedLabel}` : "Expected")}
      </div>
    );
  }

  if (status === "received") {
    return (
      <div className="flex flex-col items-start gap-0.5 min-w-0">
        <span className="inline-flex items-center gap-1 text-[12px] text-[var(--muted,#6b6a66)]">
          <svg
            className="h-3.5 w-3.5 flex-shrink-0 text-[var(--good,#1f6b34)]"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          {invoiceDate !== "—" ? `Invoice ${invoiceDate}` : "Invoice"}
        </span>
      </div>
    );
  }

  if (status === "missing") {
    const lateText =
      expectedLabel && daysLate != null
        ? `Expected ${expectedLabel}, ${daysLate} day${daysLate === 1 ? "" : "s"} late`
        : expectedLabel
          ? `Expected ${expectedLabel}`
          : null;
    return (
      <div className="flex flex-col items-start gap-0.5 min-w-0">
        {pill(
          "bg-[var(--crit-soft,#fbe6e6)] text-[var(--crit,#c0262b)]",
          <>
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--crit,#c0262b)]" />
            Missing
          </>,
        )}
        {lateText ? mutedSub(lateText) : null}
      </div>
    );
  }

  let label = "Received";
  let pillClass = "bg-[var(--good-soft,#e8f3ea)] text-[var(--good,#1f6b34)]";
  let dotClass = "bg-[var(--good,#1f6b34)]";
  let sub = invoiceDate !== "—" ? `Invoice ${invoiceDate}` : null;

  if (status === "closed") {
    label = "Closed";
    pillClass = "bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)]";
    dotClass = "bg-[var(--faint,#9a9a95)]";
    sub = lastInvoiceDate !== "—" ? `Last invoice ${lastInvoiceDate}` : null;
  } else if (status === "new") {
    label = "New";
    pillClass = "bg-[var(--warn-soft,#fbf1e0)] text-[var(--warn,#b8791e)]";
    dotClass = "bg-[var(--warn,#b8791e)]";
    sub = "No history yet";
  } else if (status === "credit") {
    label = "Credit";
    pillClass = "bg-sky-50 text-sky-700";
    dotClass = "bg-sky-500";
    sub = invoiceDate !== "—" ? `Invoice ${invoiceDate}` : null;
  } else if (status === "needs_review") {
    label = "Needs review";
    pillClass = "bg-[var(--crit-soft,#fbe6e6)] text-[var(--crit,#c0262b)]";
    dotClass = "bg-[var(--crit,#c0262b)]";
    sub = invoiceDate !== "—" ? `Invoice ${invoiceDate}` : null;
  }

  return (
    <div className="flex flex-col items-start gap-0.5 min-w-0">
      {pill(pillClass, (
        <>
          <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
          {label}
        </>
      ))}
      {sub ? mutedSub(sub) : null}
    </div>
  );
};

export default InvoiceStatusCell;
